import * as React from 'react'
import { Route } from 'react-router-dom'

import { setExperiments } from '../../common/actions/experiments'
import { rafReportPageView, reportRouteChange } from '../../common/analytics'
import useFetchBannerData from '../../common/hooks/useFetchBannerData'
import useLocationHashScroll from '../../common/hooks/useLocationHashScroll'
import ga from '../../common/utils/ga'
import {
  callBeforePageViewHandlers,
  fetchAllRequests
} from '../../common/utils/location/router'
import { ICommonLocation } from '../../common/utils/location/types'
import { Props as IPendingNavDataLoaderProps } from '../containers/pendingNavDataLoaderContainer'

interface IProps extends IPendingNavDataLoaderProps {
  children: React.ReactNode
}

const PendingNavDataLoader = ({
  store,
  routes,
  alerts,
  errorPage,
  resetAlerts,
  resetErrorPage,
  setCanonicalPath,
  setUserSearchResultsViewType,
  location,
  children
}: IProps) => {
  const [prevLocation, setPrevLocation] = React.useState<
    ICommonLocation | undefined
  >(undefined)

  // banner data fetching
  useFetchBannerData({ store, location, routes })

  // location hash scroll into
  useLocationHashScroll(location)

  // initial render
  React.useEffect(
    () => {
      if (!prevLocation) {
        store.dispatch(setExperiments())
        callBeforePageViewHandlers({
          store,
          location,
          routes,
          prevLocation
        })
          .then(() => rafReportPageView())
          .catch(() => rafReportPageView())
        setPrevLocation(location)
      }
    },
    [location]
  )

  // route update
  React.useEffect(
    () => {
      if (!prevLocation) {
        return
      }
      // https://developers.intercom.com/installing-intercom/docs/intercom-javascript#section-intercomupdate
      if ('Intercom' in window) {
        Intercom('update')
      }

      // Reset / hide alerts when changing the route after first rendering
      if (alerts.length) {
        resetAlerts()
      }

      // Reset (remove) error page if present
      if (errorPage) {
        resetErrorPage()
      }

      // Reset canonical path to default value
      setCanonicalPath(location.pathname)

      if (
        typeof window !== 'undefined' &&
        location.query.view && // query is of a type of `[propName: string]: string | string[]`
        // so it can be potentially an array
        !Array.isArray(location.query.view)
      ) {
        setUserSearchResultsViewType(location.query.view)
      }

      // Update page location for GA
      ga('set', 'location', window.location.href)

      // Fetch request for route
      const promiseRequests = fetchAllRequests({
        store,
        location,
        routes
      })

      const report = () => {
        callBeforePageViewHandlers({
          store,
          location,
          routes,
          prevLocation
        })
          .then(() => {
            rafReportPageView()
            reportRouteChange()
          })
          .catch(() => {
            rafReportPageView()
            reportRouteChange()
          })
      }

      Promise.all(promiseRequests)
        .then(report)
        .catch(report)

      setPrevLocation(location)
    },
    [location]
  )

  return <Route location={location} render={() => children} />
}

export default PendingNavDataLoader
